import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ChecklistDuplicatorProps {
  isOpen: boolean;
  onClose: () => void;
  checklist: {
    id: number;
    name: string;
    service_id: number;
  };
  services: Array<{
    id: number;
    name: string;
  }>;
}

export function ChecklistDuplicator({
  isOpen,
  onClose,
  checklist,
  services,
}: ChecklistDuplicatorProps) {
  const [targetServiceId, setTargetServiceId] = useState<string>("");
  const [newChecklistName, setNewChecklistName] = useState(`${checklist.name} (Copy)`);
  const targetServiceIdFieldId = useId();
  const newChecklistNameId = useId();

  const queryClient = useQueryClient();
  const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

  const duplicateChecklistMutation = useMutation({
    mutationFn: async (data: { target_service_id: number; new_name: string }) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/${checklist.id}/duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to duplicate checklist");
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Invalidate checklists for the target service
      queryClient.invalidateQueries({
        queryKey: ["service-checklists", Number.parseInt(targetServiceId, 10)],
      });
      toast.success(
        `Checklist duplicated successfully! ${data.data.components_copied} components copied.`
      );
      handleReset();
      onClose();
    },
    onError: () => {
      toast.error("Failed to duplicate checklist");
    },
  });

  const handleReset = () => {
    setTargetServiceId("");
    setNewChecklistName(`${checklist.name} (Copy)`);
  };

  const handleSubmit = () => {
    if (!(targetServiceId && newChecklistName.trim())) {
      toast.error("Please select a target service and enter a checklist name");
      return;
    }

    if (Number.parseInt(targetServiceId, 10) === checklist.service_id) {
      toast.error("Cannot duplicate checklist to the same service");
      return;
    }

    duplicateChecklistMutation.mutate({
      target_service_id: Number.parseInt(targetServiceId, 10),
      new_name: newChecklistName.trim(),
    });
  };

  // Filter out the current service from available options
  const availableServices = services.filter((service) => service.id !== checklist.service_id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate Checklist
          </DialogTitle>
          <DialogDescription>
            Copy "{checklist.name}" to another renovation service
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor={targetServiceIdFieldId}>Target Service</Label>
            <Select value={targetServiceId} onValueChange={setTargetServiceId}>
              <SelectTrigger id={targetServiceIdFieldId}>
                <SelectValue placeholder="Select a service..." />
              </SelectTrigger>
              <SelectContent>
                {availableServices.map((service) => (
                  <SelectItem key={service.id} value={service.id.toString()}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor={newChecklistNameId}>New Checklist Name</Label>
            <Input
              id={newChecklistNameId}
              value={newChecklistName}
              onChange={(e) => setNewChecklistName(e.target.value)}
              placeholder="Enter name for duplicated checklist..."
            />
          </div>

          <div className="bg-muted/50 p-3 rounded-lg text-sm">
            <p className="font-medium mb-1">What will be copied:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Checklist name and description</li>
              <li>All form components</li>
              <li>Component options and settings</li>
              <li>Field configurations</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={duplicateChecklistMutation.isPending}>
              {duplicateChecklistMutation.isPending ? "Duplicating..." : "Duplicate Checklist"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
